import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Container } from "reactstrap";
import Breadcrumbs from "src/components/Common/Breadcrumb";
import RichTable, { RichColumn } from "src/components/Page/RichTable";
import ColumnButton from "src/components/Page/GridButtons/ColumnButton";
import { useAppDispatch } from "src/store";
import { getClientDetails, getClientKpis, getClients } from "src/store/clients/saga";
import { ClientDetailDto, ClientDto, ClientKpiDto } from "src/store/clients/type";
import { GetLocaleDate } from "src/helpers/getLocaleDate";
import ClientEditForm from "./edit";
import ClientCreateForm from "./create";

const ClientPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState<ClientDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selected, setSelected] = useState<ClientDetailDto>();
  // KPI'lar TEK sorguda geliyor (satır başına istek atmıyoruz), sonra
  // ClientId ile listeye eşleniyor.
  const [kpis, setKpis] = useState<Record<string, ClientKpiDto>>({});
  const [onlyIdle, setOnlyIdle] = useState(false);

  document.title = t("Clients.MetaTag");

  const fetchData = () => {
    setLoading(true);
    dispatch(
      getClients({
        params: "?Skip=0&Take=1000",
        payload: {
          onSuccess: (_m: any, p: ClientDto[]) => {
            setData(Array.isArray(p) ? p : []);
            setLoading(false);
          },
          onError: () => setLoading(false),
          onFinally: () => setLoading(false),
        },
      })
    );
  };
  const fetchKpis = () => {
    dispatch(
      getClientKpis({
        payload: {
          onSuccess: (_m: any, p: ClientKpiDto[]) =>
            setKpis(Object.fromEntries((p ?? []).map((k) => [k.ClientId, k]))),
          onError: () => {},
        },
      })
    );
  };
  useEffect(() => { fetchData(); fetchKpis(); }, []);

  const daysSince = (v?: string | null) =>
    v ? Math.floor((Date.now() - new Date(v).getTime()) / 86400000) : null;

  /** Sağlık: son giriş + son 30 gün sözleşme + varlık var mı. */
  const health = (k?: ClientKpiDto) => {
    if (!k) return { tone: "secondary", label: "—" };
    const d = daysSince(k.LastLoginTime);
    if (k.Closed) return { tone: "dark", label: t("Clients.Kpi.Closed") };
    if (k.Suspended) return { tone: "danger", label: t("Clients.Kpi.Suspended") };
    if (d === null) return { tone: "secondary", label: t("Clients.Kpi.NeverLoggedIn") };
    if (d > 30) return { tone: "danger", label: t("Clients.Kpi.Idle") };
    if (k.ContractCountLast30Days > 0) return { tone: "success", label: t("Clients.Kpi.Active") };
    return { tone: "warning", label: t("Clients.Kpi.LoggedInNoActivity") };
  };

  const columns: RichColumn<ClientDto>[] = [
    { key: "ClientName", label: t("Clients.Name") },
    { key: "SectorName", label: t("Clients.Sector") },
    { key: "CorporateEmail", label: t("Clients.CorporateEmail") },
    { key: "CorporatePhone", label: t("Clients.CorporatePhone") },
    { key: "CorporateRegisteredTaxOffice", label: t("Clients.TaxOffice") },
    { key: "CorporateRegisteredTaxNumber", label: t("Clients.TaxNumber") },
    {
      key: "CreateTime",
      label: t("Clients.CreateTime"),
      render: (r) => GetLocaleDate((r as any).CreateTime),
    },
    {
      key: "LastLoginTime",
      label: t("Clients.Kpi.LastLogin"),
      render: (r) => {
        const k = kpis[(r as any).Id];
        if (!k?.LastLoginTime) return <span className="text-muted">—</span>;
        const d = daysSince(k.LastLoginTime);
        return (
          <span className={d !== null && d > 30 ? "text-danger" : ""}>
            {GetLocaleDate(k.LastLoginTime)}
          </span>
        );
      },
    },
    { key: "AssetCount", label: t("Clients.Kpi.Assets"), render: (r) => kpis[(r as any).Id]?.AssetCount ?? "—" },
    { key: "ReservationCount", label: t("Clients.Kpi.Reservations"), render: (r) => kpis[(r as any).Id]?.ReservationCount ?? "—" },
    { key: "ContractCount", label: t("Clients.Kpi.Contracts"), render: (r) => kpis[(r as any).Id]?.ContractCount ?? "—" },
    {
      key: "Health",
      label: t("Clients.Kpi.Health"),
      render: (r) => {
        const h = health(kpis[(r as any).Id]);
        return <span className={"badge bg-" + h.tone}>{h.label}</span>;
      },
    },
  ];

  // Faz 5 süzgeci: hiç girmemiş ya da 30 gündür sessiz olanlar.
  const rows = onlyIdle
    ? data.filter((r) => {
        const k = kpis[(r as any).Id];
        const d = daysSince(k?.LastLoginTime);
        return d === null || d > 30;
      })
    : data;

  const openEdit = (id: string) => {
    dispatch(
      getClientDetails({
        id,
        payload: {
          onSuccess: (_m: any, p: ClientDetailDto) => {
            setSelected(p);
            setShowEdit(true);
          },
          onError: () => {},
        },
      })
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <Breadcrumbs title={t("Breadcrumb.Dashboard")} breadcrumbItem={t("Clients.PageTitle")} />
            <div className="d-flex gap-2 mb-3">
              <button
                className={"btn " + (onlyIdle ? "btn-danger" : "btn-outline-danger")}
                onClick={() => setOnlyIdle((v) => !v)}
              >
                <i className="mdi mdi-sleep" /> {t("Clients.Kpi.OnlyIdle")}
              </button>
              <button className="btn btn-warning" onClick={() => { fetchData(); fetchKpis(); }} title={t("Components.Refresh", "Yenile") as string}>
                <i className="mdi mdi-refresh" />
              </button>
              <button className="btn btn-success text-light" onClick={() => setShowCreate(true)}>
                <i className="bx bx-plus" /> {t("Clients.AddButton")}
              </button>
            </div>
          </div>

          <RichTable<ClientDto>
            tableId="clients"
            t={t as any}
            columns={columns}
            rows={rows}
            loading={loading}
            rowKey={(r) => (r as any).Id}
            emptyText={t("Clients.Empty", "Müşteri bulunamadı") as string}
            actions={(r) => (
              <>
                <ColumnButton buttonType="details" onClick={() => navigate("/clients/details/1/" + (r as any).Id)} />
                <ColumnButton buttonType="edit" onClick={() => openEdit((r as any).Id)} />
              </>
            )}
          />
        </Container>
      </div>

      <ClientCreateForm
        open={showCreate}
        onHide={() => setShowCreate(false)}
        onSuccessClick={(_m: any, p: any) => navigate("/clients/details/1/" + p.Id)}
      />
      {selected && (
        <ClientEditForm
          data={selected}
          open={showEdit}
          onHide={() => {
            setShowEdit(false);
            setSelected(undefined);
          }}
          onSuccessClick={() => {
            setShowEdit(false);
            setSelected(undefined);
            fetchData();
          }}
        />
      )}
    </React.Fragment>
  );
};

export default ClientPage;
